/**
 * Octobre rose: the one place that knows the campaign exists.
 *
 * THE WHOLE POINT OF THIS FILE is that it can be deleted. Nothing outside it,
 * the stylesheet beside it and the one line in app.component.ts knows anything
 * about October. Removing the campaign in November is deleting two files and a
 * line, not hunting a colour through fifty screens. A ticket dated 1 November
 * 2026 says exactly that, at Maude's request on 23 September 2026.
 *
 * TWO ANSWERS, AND THE ORDER MATTERS.
 *
 * The switch first. It is a system setting, read from the API, and it can say
 * yes or no. It exists because once a version is in the stores there is no
 * other way to stop the dressing: a colour that turns out unreadable, or the
 * operation called off because the foundation did not agree, would otherwise
 * stay on every phone until November. One click, and it is gone in minutes.
 *
 * The dates second, when the API says nothing. Somebody in a tunnel, or on the
 * day the API is down, still gets the right answer, and the campaign still
 * ends by itself on 1 November whatever happens to the server.
 *
 * THE DATE IS THE PHONE'S, and that is deliberate. October starts when it is
 * October where the member is, which is what anybody would expect. It also
 * means a phone set to the wrong date sees the dressing outside October; that
 * is rare, harmless, and not worth a server round trip to prevent.
 */

/** The setting the API is asked for. Absent means "nothing to say". */
export const OCTOBER_ROSE_SETTING = 'OCTOBER_ROSE';

/** First moment of 1 October 2026, local time. */
const OPENS = new Date(2026, 9, 1, 0, 0, 0, 0);
/** First moment of 1 November 2026, local time. The campaign ends before it. */
const CLOSES = new Date(2026, 10, 1, 0, 0, 0, 0);

/** What the switch can say. */
export type OctobreRoseSwitch = 'on' | 'off' | 'unset';

/**
 * Whether today falls inside the campaign, by the phone's own clock.
 *
 * Exported on its own so a test can ask about any day without pretending to
 * be the whole application.
 */
export const isDuringOctobreRose = (now: Date = new Date()): boolean =>
  now.getTime() >= OPENS.getTime() && now.getTime() < CLOSES.getTime();

/**
 * Reads what the API answered into one of three words.
 *
 * Anything that is not plainly a yes or a no is `unset`: a missing setting, an
 * empty string, a value somebody typed by hand into the back office and got
 * wrong. Never guess in either direction from a value nobody meant.
 */
export const readOctobreRoseSwitch = (value: unknown): OctobreRoseSwitch => {
  const said = String(value ?? '').trim().toLowerCase();

  if (said === 'true' || said === '1' || said === 'on') {
    return 'on';
  }
  if (said === 'false' || said === '0' || said === 'off') {
    return 'off';
  }
  return 'unset';
};

/** The class the application wears while the campaign is on. */
export const OCTOBRE_ROSE_CLASS = 'octobre-rose';

/**
 * The answer the application acts on: the switch when it said something, the
 * dates when it did not.
 */
export const wearsOctobreRose = (
  said: OctobreRoseSwitch,
  now: Date = new Date(),
): boolean => {
  if (said === 'on') {
    return true;
  }
  if (said === 'off') {
    return false;
  }
  return isDuringOctobreRose(now);
};
